import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useAuthStore } from "../store/authStore";
import { savePostAuthRedirect } from "../utils/postAuthRedirect";

// API configuration
const API_BASE_URL =
  (import.meta as any).env.VITE_API_URL || "http://localhost:5000/api";

// Create axios instance. withCredentials sends the httpOnly auth cookies on
// every request, so the JWTs no longer need to live in JS-readable storage.
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const state = useAuthStore.getState();

    if (state.accessToken) {
      config.headers.Authorization = `Bearer ${state.accessToken}`;
    }

    // Remove Content-Type header for FormData to let browser set it automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/') || 
                          originalRequest.url?.endsWith('/auth/login') ||
                          originalRequest.url?.endsWith('/auth/register');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Another request is already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          originalRequest.headers.Authorization = `Bearer ${useAuthStore.getState().accessToken}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { setTokens, logout } = useAuthStore.getState();

      try {
        // The refresh token lives in an httpOnly cookie, sent automatically via
        // withCredentials — no need to read it from JS storage.
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );

        const responseData = response.data.data || response.data;
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = responseData;

        if (!newAccessToken) {
          throw new Error('Invalid token refresh response');
        }

        // Keep the fresh access token in memory (also re-set as an httpOnly
        // cookie by the server); it is never persisted to localStorage.
        setTokens(newAccessToken, newRefreshToken || '');

        // Process the failed queue
        failedQueue.forEach(({ resolve }) => resolve());
        failedQueue = [];

        // Retry original request (the cookie carries the new token; the header
        // is a harmless fallback).
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        // Process the failed queue with rejections
        failedQueue.forEach(({ reject }) => reject(refreshError));
        failedQueue = [];

        // Refresh failed (refresh cookie missing/expired) — logout.
        logout();

        // Only redirect if not already on login page to prevent refresh
        if (!window.location.pathname.includes('/login')) {
          // Preserve the current in-app location so the user can be returned
          // here after re-authenticating — but never store an auth page as the
          // target (would create a redirect loop / land the user on /login).
          const currentPath = window.location.pathname + window.location.search;
          const isAuthPage = /^\/(login|register|verify-email|forgot-password|reset-password)/.test(
            window.location.pathname,
          );
          if (!isAuthPage) {
            savePostAuthRedirect(currentPath);
          }
          window.location.href = "/login?expired=true";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  requestId?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    nextCursor?: string;
  };
}

// Generic API methods
export const apiGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data as T;
};

export const apiPost = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data.data as T;
};

export const apiPut = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data.data as T;
};

export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.delete<ApiResponse<T>>(url, config);
  return response.data.data as T;
};

export const apiPatch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data.data as T;
};

export { api };
